import { CoordWaypoint, Waypoint } from '@/domain/models'
import { Coordinates } from '@/domain/protocols/Coordinates'

import { RouterPaths } from '@/presentation/router/RouterPathsMapper'
import { convertDecimalDegreesToDMS } from '@/utils/coordinates'

export function isGenericCoordWaypoint(waypoint: Waypoint): waypoint is CoordWaypoint {
  return waypoint instanceof CoordWaypoint && !waypoint.extra?.isUserWaypoint && !waypoint.extra?.isAddress
}

const USER_WAYPOINTS_PATH = `${RouterPaths.MY_CONTENT}/${RouterPaths.MY_CONTENT__USER_WAYPOINTS}`

export interface UserWaypointNavigationState extends Record<string, string> {
  returnTo: string
}

/**
 * Estado da navegação para a tela de criar/editar ponto: guarda a tela em que o usuário está agora, para
 * a tela do ponto voltar para ela ao salvar ou excluir. Quem abre a tela fora da lista de pontos (mapa,
 * busca) deve passar esse estado.
 */
export function getUserWaypointReturnState(): UserWaypointNavigationState {
  return { returnTo: `${window.location.pathname}${window.location.search}` }
}

/**
 * Para onde a tela de criar/editar ponto volta ao sair: a origem guardada por `getUserWaypointReturnState`
 * ou, sem ela, a lista de pontos. Navegar para um caminho do app (em vez de voltar no histórico) garante
 * que o usuário não saia do app nem fique parado na tela quando ela foi aberta por link ou após recarregar.
 */
export function getUserWaypointReturnPath(state: unknown): string {
  const returnTo = (state as Partial<UserWaypointNavigationState> | null)?.returnTo
  const isAppPath = typeof returnTo === 'string' && returnTo.startsWith('/') && !returnTo.startsWith('//')

  return isAppPath ? returnTo : USER_WAYPOINTS_PATH
}

export function getCreateUserWaypointPath(coordinates: Coordinates): string {
  const coords = encodeURIComponent(convertDecimalDegreesToDMS(coordinates.latitude, coordinates.longitude))

  return `${RouterPaths.MY_CONTENT}/${RouterPaths.MY_CONTENT__USER_WAYPOINTS}/${RouterPaths.MY_CONTENT__USER_WAYPOINTS__CREATE}?coords=${coords}`
}

export function getEditUserWaypointPath(waypoint: CoordWaypoint): string {
  // O nome e o id vão no path da URL, então precisam ser codificados para evitar que caracteres
  // especiais (ex.: a barra "/" em um nome de ponto) sejam interpretados como separadores de segmento.
  const name = encodeURIComponent(waypoint.customName ?? '')
  const id = encodeURIComponent(waypoint.id.toString())
  const coords = encodeURIComponent(
    convertDecimalDegreesToDMS(waypoint.coordinates.latitude, waypoint.coordinates.longitude)
  )

  return `${RouterPaths.MY_CONTENT}/${RouterPaths.MY_CONTENT__USER_WAYPOINTS}/edit/${name}/${id}?coords=${coords}`
}
